import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import { API_URL } from '../env';
import PushToTalkButton from '../components/PushToTalkButton';
import TTSSettingsPanel from '../components/TTSSettingsPanel';
import TTSSkipButton from '../components/TTSSkipButton';
import { speakSequence, skipAll, findBestVoice, getGenderAppropriateVoices } from '../utils/ttsService';
import { detectGenderFromProfile } from '../utils/genderUtils';
import { TTS_ENGINES } from '../constants/tts';
import { parsePatientResponse, toConciseNonVerbal } from '../utils/patientResponseParser';

const StudentSession = () => {
    const { sessionId } = useParams();
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [characterProfile, setCharacterProfile] = useState(null);
    const [showProfile, setShowProfile] = useState(false);
    const [ttsSettings, setTtsSettings] = useState(() => {
        try {
            const saved = localStorage.getItem('gp-simulator-tts-settings');
            return saved ? JSON.parse(saved) : { mode: 'off', patientVoiceURI: null, narratorVoiceURI: null, rate: 1.0 };
        } catch {
            return { mode: 'off', patientVoiceURI: null, narratorVoiceURI: null, rate: 1.0 };
        }
    });
    const [isTtsPlaying, setIsTtsPlaying] = useState(false);
    const [hoveredMessageIdx, setHoveredMessageIdx] = useState(null);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        // Fetch initial session info content
        api.get(`/api/simulator/session/${sessionId}`)
            .then(res => {
                if (res.data.character_profile) {
                    setCharacterProfile(res.data.character_profile);
                }
            })
            .catch(err => console.error("Session load error", err));

        // Initialize TTS settings from global config
        api.getTTSConfig().then(res => {
            const gc = res.data;
            setTtsSettings(prev => ({
                ...prev,
                engine: gc.engine || TTS_ENGINES.BROWSER,
                autoGenderSelect: gc.auto_gender_select,
                patientVoiceId: gc.patient_voice_id,
                narratorVoiceId: gc.narrator_voice_id,
                rate: gc.default_rate || 1.0,
                mode: gc.default_narration || 'all'
            }));
        }).catch(err => {
            console.error("Failed to fetch global TTS config:", err);
            // Fallback to local defaults if API fails
            setTtsSettings(prev => ({ ...prev, engine: TTS_ENGINES.BROWSER }));
        });

        return () => {
            skipAll();
        };
    }, [sessionId]);

    const sendMessage = async (e, textOverride = null) => {
        if (e) e.preventDefault();
        const textToSend = textOverride !== null ? textOverride : input;

        if (!textToSend.trim() || loading) return;

        skipAll(); // Cancel any ongoing TTS when sending new message

        const userMsg = { role: 'student', content: textToSend };
        setMessages(prev => [...prev, userMsg]);
        setInput('');
        setLoading(true);



        try {
            // Prepare for streaming response
            const response = await fetch(`${API_URL}/api/simulator/session/${sessionId}/chat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    // Add auth headers if needed, currently relying on cookies
                },
                body: JSON.stringify({
                    message: userMsg.content,
                    history: messages // pass previous history
                })
            });

            if (!response.ok) throw new Error('Network response was not ok');
            if (!response.body) throw new Error('ReadableStream not supported');

            const reader = response.body.getReader();
            const decoder = new TextDecoder();

            // Add a placeholder for the patient response
            setMessages(prev => [...prev, { role: 'patient', content: '' }]);

            let done = false;
            let accumulatedText = "";
            let buffer = "";

            while (!done) {
                const { value, done: doneReading } = await reader.read();
                done = doneReading;
                const chunk = decoder.decode(value, { stream: true });
                buffer += chunk;

                // Process SSE events
                const lines = buffer.split('\n\n');
                buffer = lines.pop(); // Keep remainder

                let textMatched = false;
                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const jsonStr = line.slice(6);
                            const data = JSON.parse(jsonStr);

                            if (data.text) {
                                accumulatedText += data.text;
                                textMatched = true;
                            }
                            if (data.error) console.error("Stream error val:", data.error);

                        } catch (e) {
                            console.warn("SSE Parse Error", e);
                        }
                    }
                }

                if (textMatched) {
                    const currentText = accumulatedText;
                    setMessages((prevMsgs) => {
                        const newMsgs = [...prevMsgs];
                        const lastMsg = newMsgs[newMsgs.length - 1];
                        if (lastMsg && lastMsg.role === 'patient') {
                            return [
                                ...newMsgs.slice(0, -1),
                                { ...lastMsg, content: currentText }
                            ];
                        }
                        return newMsgs;
                    });
                }
            }

            // After streaming completes, parse and play TTS
            const { segments, displayText } = parsePatientResponse(accumulatedText);

            // Update display with clean text
            setMessages(prev => {
                const newMsgs = [...prev];
                const lastMsg = newMsgs[newMsgs.length - 1];
                if (lastMsg.role === 'patient') {
                    newMsgs[newMsgs.length - 1] = {
                        ...lastMsg,
                        content: displayText,
                        segments
                    };
                }
                return newMsgs;
            });

            // Handle TTS playback
            if (ttsSettings.mode !== 'off') {
                const patientGender = detectGenderFromProfile(characterProfile);

                let pVoice = null;
                let nVoice = null;

                if (ttsSettings.autoGenderSelect) {
                    const { patientVoice, narratorVoice } = await getGenderAppropriateVoices(
                        ttsSettings.engine || TTS_ENGINES.BROWSER,
                        patientGender
                    );
                    pVoice = patientVoice;
                    nVoice = narratorVoice;
                } else {
                    pVoice = ttsSettings.engine === TTS_ENGINES.BROWSER ? ttsSettings.patientVoiceURI : ttsSettings.patientVoiceId;
                    nVoice = ttsSettings.engine === TTS_ENGINES.BROWSER ? ttsSettings.narratorVoiceURI : ttsSettings.narratorVoiceId;
                }

                // Fallback to auto-select if still null
                if (!pVoice) {
                    const best = await findBestVoice(patientGender, 'en-GB', { engine: ttsSettings.engine || TTS_ENGINES.BROWSER });
                    pVoice = best?.id || best?.voiceURI;
                }

                const baseRate = ttsSettings.rate || 1.0;
                // Narrator slightly slower relative to base rate, but bounded
                const narratorRate = Math.max(0.5, Math.min(2.0, baseRate * 0.9));

                const ttsSegments = segments
                    .filter(seg => ttsSettings.mode === 'all' || seg.type === 'verbal')
                    .map(seg => ({
                        text: seg.tts,
                        voiceId: seg.type === 'verbal' ? pVoice : nVoice,
                        options: seg.type === 'verbal'
                            ? { rate: baseRate, pitch: 1.0 }
                            : { rate: narratorRate, pitch: 0.8 } // Narrator: slightly slower, lower pitch
                    }));

                if (ttsSegments.length > 0) {
                    setIsTtsPlaying(true);
                    try {
                        await speakSequence(ttsSegments);
                    } catch (err) {
                        console.error('Sequence playback failed:', err);
                    } finally {
                        setIsTtsPlaying(false);
                    }
                }
            }

        } catch (error) {
            console.error('Error:', error);
            setMessages(prev => [...prev, { role: 'system', content: 'Connection error. Please try again.' }]);
        } finally {
            setLoading(false);
        }
    };

    const handleTranscriptComplete = (text) => {
        if (text && text.trim()) {
            setInput(text);
            sendMessage(null, text);
        }
    };

    const handleDeleteMessage = (index) => {
        if (loading) return;

        if (!window.confirm('Delete this message and retry from here? All subsequent dialogue will be removed.')) {
            return;
        }

        skipAll(); // Cancel any TTS
        setIsTtsPlaying(false);
        setMessages(prev => prev.slice(0, index));
    };

    const handleStopTTS = () => {
        skipAll();
        setIsTtsPlaying(false);
    };

    const renderMessageContent = (msg) => {
        if (msg.role !== 'patient') {
            return <span style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</span>;
        }

        if (!Array.isArray(msg.segments) || msg.segments.length === 0) {
            return <span style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</span>;
        }

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '6px' }}>
                {msg.segments.map((segment, segmentIndex) => {
                    if (segment.type === 'nonverbal') {
                        const cueText = toConciseNonVerbal(segment.display || segment.tts);
                        if (!cueText) return null;
                        return (
                            <span
                                key={segmentIndex}
                                style={{
                                    color: '#2e7d32',
                                    fontStyle: 'italic',
                                    fontSize: '13px',
                                    lineHeight: 1.4
                                }}
                            >
                                [{cueText}]
                            </span>
                        );
                    }

                    return (
                        <span
                            key={segmentIndex}
                            style={{
                                color: '#0b5fff',
                                fontWeight: 700,
                                background: '#eaf2ff',
                                borderLeft: '4px solid #0b5fff',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                lineHeight: 1.45
                            }}
                        >
                            {segment.display}
                        </span>
                    );
                })}
            </div>
        );
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '80vh', maxWidth: '900px', margin: '0 auto' }}>
            <div style={{ padding: '20px', borderBottom: '1px solid #ccc', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <h2>GP Consultation Room</h2>
                    <p>You are the GP. Conduct a history taking with the patient.</p>
                </div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <TTSSkipButton
                        isPlaying={isTtsPlaying}
                        onSkip={handleStopTTS}
                    />
                    <TTSSettingsPanel settings={ttsSettings} onSettingsChange={setTtsSettings} />
                    {characterProfile && (
                        <button
                            onClick={() => setShowProfile(true)}
                            style={{
                                padding: '8px 16px',
                                background: '#6c757d',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                        >
                            View Patient Background
                        </button>
                    )}
                </div>
            </div>

            {/* Profile Modal */}
            {showProfile && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div className="card-bg" style={{
                        padding: '20px',
                        borderRadius: '8px',
                        maxWidth: '600px',
                        width: '90%',
                        maxHeight: '80vh',
                        overflowY: 'auto'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                            <h3 style={{ margin: 0 }}>Patient Background (Teacher View)</h3>
                            <button
                                onClick={() => setShowProfile(false)}
                                style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' }}
                            >
                                &times;
                            </button>
                        </div>
                        <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>
                            {characterProfile}
                        </div>
                    </div>
                </div>
            )}

            <div className="secondary-bg" style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
                {messages.length === 0 && <p className="text-muted" style={{ textAlign: 'center' }}>Consultation started. Introduce yourself to the patient.</p>}

                {messages.map((msg, idx) => (
                    <div
                        key={idx}
                        onMouseEnter={() => setHoveredMessageIdx(idx)}
                        onMouseLeave={() => setHoveredMessageIdx(null)}
                        style={{
                            marginBottom: '15px',
                            textAlign: msg.role === 'student' ? 'right' : 'left'
                        }}
                    >
                        <div style={{
                            display: 'inline-block',
                            padding: '10px 15px',
                            borderRadius: '10px',
                            background: msg.role === 'student' ? '#0070f3' : (msg.role === 'system' ? '#ff4d4f' : 'white'),
                            color: msg.role === 'student' ? 'white' : 'black',
                            maxWidth: '70%',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            position: 'relative'
                        }}>
                            {msg.role === 'student' && !loading && hoveredMessageIdx === idx && (
                                <button
                                    onClick={() => handleDeleteMessage(idx)}
                                    title="Delete and retry from here"
                                    style={{
                                        position: 'absolute',
                                        top: '-8px',
                                        right: '-8px',
                                        width: '20px',
                                        height: '20px',
                                        borderRadius: '50%',
                                        border: 'none',
                                        background: '#dc3545',
                                        color: 'white',
                                        fontSize: '12px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        opacity: 0.8,
                                        zIndex: 5
                                    }}
                                >
                                    &times;
                                </button>
                            )}
                            <strong>{msg.role === 'student' ? 'GP' : (msg.role === 'system' ? 'System' : 'Patient')}: </strong>
                            {renderMessageContent(msg)}
                        </div>
                    </div>
                ))}
                <div ref={messagesEndRef} />
                <style>{`
                    @keyframes pulse-bg {
                        0% { opacity: 0.8; }
                        50% { opacity: 1; }
                        100% { opacity: 0.8; }
                    }
                `}</style>
            </div>

            <form onSubmit={sendMessage} className="card-bg" style={{ padding: '20px', borderTop: '1px solid var(--border-color)', display: 'flex', gap: '10px', alignItems: 'center' }}>
                <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Ask a question..."
                    className="input-bg"
                    style={{ flex: 1, padding: '10px', fontSize: '16px', borderRadius: '4px' }}
                    disabled={loading}
                    autoFocus
                />

                <PushToTalkButton
                    onTranscriptComplete={handleTranscriptComplete}
                    disabled={loading}
                    apiUrl={API_URL}
                />

                <button
                    type="submit"
                    disabled={loading}
                    style={{ padding: '10px 20px', background: '#0070f3', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                >
                    Send
                </button>
            </form>
        </div>
    );
};

export default StudentSession;
